// adminfrontend/src/admin_ui/pages/AdminGoodsReceiptsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminPurchasingApi from "../services/adminPurchasingApi";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function AdminGoodsReceiptsPage({ adminEmail, onLogout }) {
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);

  // create GR
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [poLoaded, setPoLoaded] = useState(null); // { purchaseOrder, items }
  const [poLoading, setPoLoading] = useState(false);

  const [branchId, setBranchId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [postNow, setPostNow] = useState(true);

  // items: auto-filled from PO
  const [items, setItems] = useState([]);

  // list filters
  const [status, setStatus] = useState("");
  const [filterBranchId, setFilterBranchId] = useState("");

  // list
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [limit] = useState(100);
  const [offset] = useState(0);

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  function setItem(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  async function loadBranches() {
    const data = await adminInventoryApi.listBranches();
    if (!data || data.ok !== true) throw new Error("branches_failed");
    const bs = data.branches || [];
    setBranches(bs);
    if (!branchId && bs[0]) setBranchId(String(bs[0].id));
    return bs;
  }

  async function loadLocations(bId) {
    if (!bId) {
      setLocations([]);
      return;
    }
    const data = await adminInventoryApi.listLocations(bId);
    if (!data || data.ok !== true) throw new Error("locations_failed");
    const ls = data.locations || [];
    setLocations(ls);
    if (!locationId && ls[0]) setLocationId(String(ls[0].id));
  }

  async function loadGRs() {
    setLoading(true);
    setError("");
    try {
      const data = await adminPurchasingApi.listGoodsReceipts({
        limit,
        offset,
        status: status || undefined,
        branchId: filterBranchId ? Number(filterBranchId) : undefined,
      });

      if (!data || data.ok !== true) throw new Error("gr_failed");

      const list = data.goodsReceipts || data.items || data.rows || [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(data.total || list.length || 0));
    } catch (e) {
      console.error("[AdminGoodsReceiptsPage] load error:", e);
      setError("Unable to load goods receipts. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const bs = await loadBranches();
        const effectiveBranch = branchId || (bs[0] ? String(bs[0].id) : "");
        if (effectiveBranch) await loadLocations(effectiveBranch);
        await loadGRs();
      } catch (e) {
        console.error("[AdminGoodsReceiptsPage] init error:", e);
        setError("Unable to load branches/locations or goods receipts.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadLocations(branchId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  async function onLoadPO() {
    setOkMsg("");
    setError("");
    setPoLoading(true);
    try {
      const id = Number(String(purchaseOrderId).replace(/[^\d]/g, ""));
      if (!id) throw new Error("invalid_po_id");

      const data = await adminPurchasingApi.getPurchaseOrder(id);
      if (!data || data.ok !== true) throw new Error("po_fetch_failed");

      const po = data.purchaseOrder;
      const lines = Array.isArray(data.items) ? data.items : [];

      setPoLoaded({ purchaseOrder: po, items: lines });

      // lock branch to PO branch
      const poBranchId = String(po.branch_id || po.branchId || "");
      if (poBranchId) setBranchId(poBranchId);

      const mapped = lines
        .map((l) => {
          const ordered = num(l.quantity_ordered);
          const received = num(l.quantity_received);
          const remaining = Math.max(0, ordered - received);
          return {
            purchaseOrderItemId: String(l.id),
            productId: String(l.product_id),
            productName: l.product_name || "",
            sku: l.product_sku || "",
            remaining,
            quantityReceived: remaining > 0 ? remaining : 0,
          };
        })
        .filter((x) => x.remaining > 0);

      setItems(mapped.length ? mapped : []);
      if (!mapped.length) {
        setOkMsg("PO loaded. Nothing remaining to receive (all lines fully received).");
      } else {
        setOkMsg(`PO loaded. ${mapped.length} open line(s) ready to receive.`);
      }
    } catch (e) {
      console.error("[AdminGoodsReceiptsPage] load PO error:", e);
      setPoLoaded(null);
      setItems([]);
      setError("Unable to load PO. Check the PO ID and ensure it exists.");
    } finally {
      setPoLoading(false);
    }
  }

  async function onCreate(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
      const poId = Number(String(purchaseOrderId).replace(/[^\d]/g, ""));
      if (!poId) throw new Error("bad_po");

      const cleanedItems = items
        .map((it) => ({
          purchaseOrderItemId: Number(String(it.purchaseOrderItemId || "").replace(/[^\d]/g, "")),
          productId: Number(String(it.productId || "").replace(/[^\d]/g, "")),
          quantityReceived: Number(it.quantityReceived),
          remaining: Number(it.remaining),
        }))
        .filter((it) => it.quantityReceived > 0 && it.purchaseOrderItemId > 0 && it.productId > 0);

      if (cleanedItems.length === 0) throw new Error("no_valid_items");

      // guard: prevent over-receipt
      for (const it of cleanedItems) {
        if (Number.isFinite(it.remaining) && it.quantityReceived > it.remaining) {
          throw new Error("over_receive");
        }
      }

      const payload = {
        purchaseOrderId: poId,
        branchId: Number(branchId),
        locationId: Number(locationId),
        receivedDate: receivedDate || undefined,
        notes: notes.trim() || undefined,
        postNow: Boolean(postNow),
        items: cleanedItems.map(({ remaining, ...rest }) => rest),
      };

      const data = await adminPurchasingApi.createGoodsReceipt(payload);
      if (!data || data.ok !== true) throw new Error("create_gr_failed");

      setOkMsg(postNow ? "Goods receipt created and posted." : "Goods receipt created (draft).");
      setReceivedDate("");
      setNotes("");
      setPoLoaded(null);
      setItems([]);
      setPurchaseOrderId("");
      await loadGRs();
    } catch (e) {
      console.error("[AdminGoodsReceiptsPage] create error:", e);
      if (String(e?.message) === "over_receive") {
        setError("Qty received cannot exceed remaining qty on a PO line.");
      } else {
        setError("Unable to create goods receipt. Ensure PO, branch, location and item quantities are valid.");
      }
    } finally {
      setMutating(false);
    }
  }

  async function doPost(id) {
    setMutating(true);
    setError("");
    setOkMsg("");
    try {
      const data = await adminPurchasingApi.postGoodsReceipt(id);
      if (!data || data.ok !== true) throw new Error("post_failed");
      setOkMsg("Goods receipt posted.");
      await loadGRs();
    } catch (e) {
      console.error("[AdminGoodsReceiptsPage] post error:", e);
      setError("Unable to post goods receipt (conflict or server error).");
    } finally {
      setMutating(false);
    }
  }

  const poBranchLocked = Boolean(poLoaded?.purchaseOrder?.branch_id || poLoaded?.purchaseOrder?.branchId);

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Goods Receipts</h1>
            <p className="ucx-admin-subtitle">Load a PO, auto-fill remaining lines, then receive into a location.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={loadGRs} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Goods Receipt</div>

          <form className="ucx-admin-form" onSubmit={onCreate}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Purchase Order ID *</label>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    value={purchaseOrderId}
                    onChange={(e) => setPurchaseOrderId(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="e.g. 101"
                    required
                  />
                  <button
                    type="button"
                    className="ucx-btn ucx-btn-outline"
                    onClick={onLoadPO}
                    disabled={poLoading || !purchaseOrderId}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {poLoading ? "Loading…" : "Load PO"}
                  </button>
                </div>
              </div>

              <div className="ucx-admin-field">
                <label>Branch *</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={poBranchLocked}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
                {poBranchLocked && <div className="ucx-small">Locked to PO branch.</div>}
              </div>

              <div className="ucx-admin-field">
                <label>Location *</label>
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)} required>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.code ? `(${l.code})` : ""} [{l.type}]
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Received date</label>
                <input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} />
              </div>

              <div className="ucx-admin-field">
                <label>Notes</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              </div>

              <div className="ucx-admin-field ucx-admin-field-inline" style={{ alignItems: "flex-end" }}>
                <label style={{ minWidth: 0 }}>Post now</label>
                <input type="checkbox" checked={postNow} onChange={(e) => setPostNow(e.target.checked)} />
              </div>
            </div>

            <div className="ucx-admin-lineitems">
              <div className="ucx-admin-lineitems-head">
                <div className="ucx-admin-panel-title" style={{ marginBottom: 0 }}>
                  PO Lines to Receive
                </div>
              </div>

              <div className="ucx-admin-table-wrapper">
                <table className="ucx-admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>PO Item</th>
                      <th style={{ width: 120 }}>Product</th>
                      <th>Product name</th>
                      <th style={{ width: 140 }}>Remaining</th>
                      <th style={{ width: 160 }}>Qty receive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx}>
                        <td>{it.purchaseOrderItemId}</td>
                        <td>{it.productId}</td>
                        <td>
                          {it.productName ? (
                            <>
                              {it.productName}{" "}
                              {it.sku ? <span className="ucx-badge ucx-badge-neutral">{it.sku}</span> : null}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{it.remaining}</td>
                        <td>
                          <input
                            className="ucx-admin-input-compact"
                            type="number"
                            min="0"
                            max={it.remaining}
                            value={it.quantityReceived}
                            onChange={(e) => setItem(idx, { quantityReceived: Number(e.target.value) })}
                          />
                        </td>
                      </tr>
                    ))}

                    {items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="ucx-admin-empty">
                          Load a PO to auto-fill remaining lines.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ucx-admin-toolbar">
                <div className="ucx-admin-toolbar-hint">Tip: set Qty receive to 0 to skip a line.</div>

                <div className="ucx-admin-form-actions">
                  <button
                    type="submit"
                    className="ucx-btn ucx-btn-primary"
                    disabled={mutating || !purchaseOrderId || !branchId || !locationId || items.length === 0}
                  >
                    {mutating ? "Creating…" : postNow ? "Create & Post" : "Create Draft"}
                  </button>
                </div>
              </div>
            </div>

            {okMsg && <div className="ucx-admin-status-success">{okMsg}</div>}
            {error && <div className="ucx-admin-status-error">{error}</div>}
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="draft">draft</option>
              <option value="posted">posted</option>
              <option value="cancelled">cancelled</option>
            </select>

            <select value={filterBranchId} onChange={(e) => setFilterBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>

            <button type="button" className="ucx-btn ucx-btn-primary" onClick={loadGRs} disabled={loading}>
              Apply
            </button>

            <span className="ucx-small">
              Showing {normalizedRows.length} of {total}
            </span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>GR #</th>
                <th>PO</th>
                <th>Branch</th>
                <th>Location</th>
                <th>Status</th>
                <th>Received</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">
                    Loading goods receipts…
                  </td>
                </tr>
              )}

              {!loading && normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">
                    No goods receipts found.
                  </td>
                </tr>
              )}

              {normalizedRows.map((gr) => {
                const grStatus = (gr.status || "").toLowerCase();
                const canPost = grStatus === "draft";

                return (
                  <tr key={gr.id}>
                    <td>{gr.id}</td>
                    <td>{gr.grn_number || gr.number || "-"}</td>
                    <td>{gr.purchase_order_id || gr.purchaseOrderId || "-"}</td>
                    <td>{gr.branch_name || gr.branchId || "-"}</td>
                    <td>{gr.location_name || gr.locationId || "-"}</td>
                    <td>{gr.status || "-"}</td>
                    <td>{gr.received_date ? new Date(gr.received_date).toLocaleDateString() : "-"}</td>
                    <td>
                      <div className="ucx-admin-actions">
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-approve"
                          onClick={() => doPost(gr.id)}
                          disabled={!canPost || mutating}
                        >
                          Post
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminGoodsReceiptsPage;
